import React from "react";
import { useTranslation } from "react-i18next";
import { useDepositWithdrawContext, useUrlParamsContext } from "src/contexts";
import {
  Loader,
  CxmRouteLoader,
  CxmWithdrawalForm,
  WithdrawalForm,
  PageLayout,
  AlfredIneligibleBlocker,
  AlfredKycFallbackForm,
} from "src/components";
import { usePaymentsRoutes, useAlfredKycFallbackGate, useCxmWithdrawal, useCxmLoaderSequence } from "src/hooks";
import { isCxmDirectRoute, isCxmExchange, PaymentsRoutesResponse } from "src/api";

/**
 * CXM payout page. Shares no provider logic with the Alfred page: Alfred's
 * eligibility verdict and KYC-fallback gate are answers about an *Alfred*
 * customer, and on a CXM tenant Alfred is not configured at all — so asking
 * was both meaningless and, because the page blocked on the answer, fatal.
 * CXM enforces KYC server-side at create time instead.
 *
 * Splitting the pages is what actually keeps them apart: hooks cannot be
 * called conditionally, so branching inside one component would still run
 * Alfred's gate on every CXM render.
 */
const CxmWithdrawalPage: React.FC<{ routesError: string | null }> = ({ routesError }): JSX.Element => {
  const { t } = useTranslation();
  const { widgetDataLoading, widgetDataError } = useDepositWithdrawContext();
  // The page owns the orchestrator so the pane can wait for the method
  // catalogue too — otherwise the glass cleared onto a form whose method and
  // account selects were still empty. It also starts that request alongside
  // the widget-data one instead of after it.
  const withdrawal = useCxmWithdrawal();

  const blocked = Boolean(widgetDataError || routesError);
  const loading = widgetDataLoading || withdrawal.methodsLoading || blocked;
  const loader = useCxmLoaderSequence(loading);

  return (
    <>
      {!loading && loader.revealed && (
        <PageLayout centered>
          <CxmWithdrawalForm withdrawal={withdrawal} />
        </PageLayout>
      )}
      {loader.phase !== "done" && (
        <CxmRouteLoader
          label={t("CXM.WITHDRAWAL.LOADING")}
          leaving={loader.phase === "leaving"}
          slowLabel={t("CXM.WITHDRAWAL.LOADING_SLOW")}
        />
      )}
    </>
  );
};

/** Everything that is not CXM — unchanged, Alfred gates included. */
const AlfredWithdrawalPage: React.FC<{
  paymentsRoutes: PaymentsRoutesResponse;
  routesError: string | null;
}> = ({ paymentsRoutes, routesError }): JSX.Element => {
  const { widgetDataLoading, widgetDataError, alfredIneligibilityCode } = useDepositWithdrawContext();
  const { alfredKycFallback, loading: kycFallbackLoading } = useAlfredKycFallbackGate(alfredIneligibilityCode);

  if (widgetDataLoading || widgetDataError || routesError || kycFallbackLoading) {
    return <Loader />;
  }

  if (alfredKycFallback) {
    return <AlfredKycFallbackForm fallback={alfredKycFallback} />;
  }

  if (alfredIneligibilityCode) {
    return <AlfredIneligibleBlocker code={alfredIneligibilityCode} />;
  }

  return (
    <PageLayout centered>
      <WithdrawalForm paymentsRoutes={paymentsRoutes} />
    </PageLayout>
  );
};

/**
 * Which provider renders is decided by the route, not by the tenant.
 *
 * The tenant name was the switch until the CXM tenant turned out to run its
 * own older PSP under `CXM` on 52 routes alongside the partner gateway, so on
 * that exchange one name covers two providers and picking by name would put
 * the partner form in front of the tenant's own payment routes.
 *
 * The route has to load first, and until it does the tenant name is still the
 * right guess for *which loader* to show — that choice is cosmetic, and a CXM
 * tenant should not see a generic spinner before its own branded one.
 */
const WithdrawalPage: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const { exchange } = useUrlParamsContext();
  const { paymentsRoutes, paymentsRoutesLoading, paymentsRoutesError } = usePaymentsRoutes();

  if (paymentsRoutesLoading) {
    return isCxmExchange(exchange) ? <CxmRouteLoader label={t("CXM.WITHDRAWAL.LOADING")} /> : <Loader />;
  }

  // Resolved once here and passed down: a second `usePaymentsRoutes()` in the
  // child would refetch and put its loading state back to true, replaying the
  // branded loader from the start.
  return isCxmDirectRoute(paymentsRoutes.psp_service_id) ? (
    <CxmWithdrawalPage routesError={paymentsRoutesError} />
  ) : (
    <AlfredWithdrawalPage paymentsRoutes={paymentsRoutes} routesError={paymentsRoutesError} />
  );
};

export default WithdrawalPage;
